/**
 * Kalender: a resource calendar (owner, 2026-09-21). Names stay on the left and the days on top
 * while the grid scrolls; each absence is one block across its days, coloured by its category,
 * and a request not yet answered is dashed. A block opens the drawer; an empty day starts
 * Registrer fravær for that person and date, for somebody who may register one.
 */
import type { ApiAbsence, ApiUser } from '@/api/data';
import { T } from '@/i18n';
import { Button, Empty } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { PersonLabel } from '@/ui/PersonLabel';
import { DN, fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { dow } from '@timefly/core';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePhone } from '../vaktplan/Phone';
import { VIEWS, type View, agendaOf, blocksFor, daysFor, stepFor, weeksOf } from './calendar';
import { CATEGORIES, catInfo, catOf } from './category';
import { periodText, spanOf } from './dates';
import { type Filter, personMatches, teamsOf } from './filters';
import { CatIcon, FilterBar, StatusChip } from './parts';

export function Kalender({
  all,
  users,
  filter,
  onFilter,
  filtersOpen,
  onFiltersOpen,
  teams,
  today,
  tenantId,
  mayRegister,
  onOpen,
  onNew,
}: {
  all: ApiAbsence[];
  users: ApiUser[];
  filter: Filter;
  onFilter: (f: Filter) => void;
  filtersOpen: boolean;
  onFiltersOpen: (open: boolean) => void;
  teams: string[];
  today: string;
  tenantId: number | undefined;
  mayRegister: boolean;
  onOpen: (a: ApiAbsence) => void;
  onNew: (uid: number, day: string) => void;
}) {
  // ?visning=neste7&status=innvilget: the indicator «Fravær neste 7 dager» lands on exactly its days and
  // its approved absence (#276); the choices are the reader's from there on
  const [sp] = useSearchParams();
  const [view, setView] = useState<View>(() =>
    VIEWS.some(([k]) => k === sp.get('visning')) ? (sp.get('visning') as View) : 'uker',
  );
  const [status, setStatus] = useState<'' | 'accepted' | 'pending'>(() =>
    sp.get('status') === 'innvilget' ? 'accepted' : sp.get('status') === 'venter' ? 'pending' : '',
  );
  // #272 B-U04: under 360 px the search shares its row with Filter, and «Søk ansatt eller team» (174 px
  // drawn) is cut in 158; the short form fits whole
  const narrow = usePhone('(max-width:359px)');
  // a phone reads the absences as a list grouped by period, with whole names (Fravær V1.4, #276)
  const phone = usePhone('(max-width:800px)');
  const [anchor, setAnchor] = useState(today);
  const days = daysFor(view, anchor);
  const weeks = weeksOf(days);
  const people = users.filter((u) => personMatches(u, filter, tenantId));
  const shown = all.filter(
    (a) => (!filter.cat || catOf(a.absence_type) === filter.cat) && (!status || a.status === status),
  );
  const first = days[0] ?? today;
  const last = days[days.length - 1] ?? today;
  // a phone shows a week or so: start with today's column in view, next to the names
  const gridRef = useRef<HTMLElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the days on screen change; it reads the DOM they rendered
  useEffect(() => {
    const el = gridRef.current;
    const now = el?.querySelector<HTMLElement>('.rday.now');
    const names = el?.querySelector<HTMLElement>('.rname');
    if (el && now && names) el.scrollLeft = Math.max(0, now.offsetLeft - names.offsetWidth - 8);
  }, [first, view]);

  return (
    <>
      <FilterBar
        value={filter}
        onChange={onFilter}
        teams={teams}
        placeholder={narrow ? T('Søk ansatt') : T('Søk ansatt eller team')}
        open={filtersOpen}
        onOpen={onFiltersOpen}
      >
        <fieldset className="seg" aria-label={T('Visning')}>
          {VIEWS.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)}>
              {T(label)}
            </button>
          ))}
        </fieldset>
        <PeriodControl
          period={view === 'maned' ? 'month' : 'three'}
          value={anchor}
          onChange={setAnchor}
          prevLabel={T('Forrige')}
          nextLabel={T('Neste')}
          onPrev={() => setAnchor(stepFor(view, anchor, -1))}
          onNext={() => setAnchor(stepFor(view, anchor, 1))}
        >
          <span className="lbl">{periodText(first, last)}</span>
        </PeriodControl>
        <Button onClick={() => setAnchor(today)} disabled={today >= first && today <= last}>
          {T('I dag')}
        </Button>
        <label className="mf">
          {T('Status')}
          <select
            className="inp"
            aria-label={T('Status')}
            value={status}
            data-testid="cal-status"
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            <option value="">{T('Innvilget og ventende')}</option>
            <option value="accepted">{T('Innvilget')}</option>
            <option value="pending">{T('Venter på svar')}</option>
          </select>
        </label>
      </FilterBar>
      {people.length === 0 ? (
        <div className="mcard">
          <Empty>{T('Ingen ansatte passer filteret.')}</Empty>
        </div>
      ) : phone ? (
        <Agenda all={shown} users={people} days={days} tenantId={tenantId} onOpen={onOpen} />
      ) : (
        <div className="mcard rcalw">
          <section
            ref={gridRef}
            className="rcal"
            data-testid="strip"
            style={{ '--n': days.length } as React.CSSProperties}
            aria-label={T('Fraværskalender')}
            // UI01-20: a day reached by keyboard clears the sticky names in both directions. The browser's
            // own focus scroll left Shift+Tab's cell 14 px under them; a click is never moved
            onFocus={(e) => {
              const el = e.target as HTMLElement;
              if (!el.matches?.(':focus-visible') || el.closest('.rname')) return;
              el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
              const sc = e.currentTarget;
              const hidden =
                sc.getBoundingClientRect().left +
                (Number.parseFloat(getComputedStyle(sc).scrollPaddingLeft) || 0) -
                el.getBoundingClientRect().left;
              if (hidden > 0) sc.scrollLeft -= hidden;
            }}
          >
            <div className="rrow rhead">
              <span className="rname rcorner">{T('Ansatt')}</span>
              {weeks.map((w) => (
                <span
                  key={w.week}
                  className="rweek"
                  style={{
                    gridColumn: `${w.start + 2} / span ${w.span}`,
                    gridRow: 1,
                  }}
                >
                  {T('Uke')} {w.week}
                </span>
              ))}
              {days.map((d, i) => (
                <span
                  key={d}
                  className={`rday${dow(d) >= 5 ? ' we' : ''}${d === today ? ' now' : ''}`}
                  style={{ gridColumn: i + 2, gridRow: 2 }}
                >
                  {DN[dow(d)]}
                  <b>{Number(d.slice(8, 10))}</b>
                </span>
              ))}
            </div>
            {people.map((u) => {
              const name = fullName(u);
              return (
                <div className="rrow" key={u.id} data-testid="cal-row">
                  <span className="rname" title={name}>
                    <PersonLabel id={u.id} name={name} />
                    <small>
                      {teamsOf(u, tenantId)
                        .map((t) => t.name)
                        .join(', ')}
                    </small>
                  </span>
                  {days.map((d, i) =>
                    mayRegister ? (
                      <button
                        key={d}
                        type="button"
                        className={`rcell${dow(d) >= 5 ? ' we' : ''}${d === today ? ' now' : ''}`}
                        style={{ gridColumn: i + 2 }}
                        aria-label={`${T('Registrer fravær')} ${name} ${periodText(d, d)}`}
                        onClick={() => onNew(u.id, d)}
                      />
                    ) : (
                      // registering for somebody is the administrator's; a manager's empty day is only a day
                      <span
                        key={d}
                        className={`rcell${dow(d) >= 5 ? ' we' : ''}${d === today ? ' now' : ''}`}
                        style={{ gridColumn: i + 2 }}
                      />
                    ),
                  )}
                  {blocksFor(shown, u.id, days).map((b) => {
                    const c = catOf(b.a.absence_type);
                    const { from, to } = spanOf(b.a);
                    const pend = b.a.status === 'pending';
                    const label = `${name}, ${b.a.absence_type?.name ?? T('Fravær')} ${periodText(from, to, false)}${pend ? `, ${T('venter på svar')}` : ''}`;
                    return (
                      <button
                        key={b.a.id}
                        type="button"
                        // a label from two days up; one day is the icon; pending is the dashed border alone,
                        // with the legend's icon (#272 B-29)
                        className={`rblk tn-${catInfo(c).tone}${pend ? ' pend' : ''}${b.cutL ? ' cl' : ''}${b.cutR ? ' cr' : ''}${b.end - b.start < 1 ? ' nar' : ''}`}
                        style={{ gridColumn: `${b.start + 2} / ${b.end + 3}` }}
                        title={label}
                        aria-label={label}
                        data-testid="cal-block"
                        onClick={() => onOpen(b.a)}
                      >
                        <Icon name={catInfo(c).icon} />
                        <i className="rsh">{T(catInfo(c).short)}</i>
                        <span>
                          <b>{b.a.absence_type?.name ?? T('Fravær')}</b>
                          <small>{periodText(from, to, false)}</small>
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </section>
          <details className="fagd">
            <summary>
              {T('Se perioder med fullstendige navn')} <Icon name="chevronRight" />
            </summary>
            <Agenda all={shown} users={people} days={days} tenantId={tenantId} onOpen={onOpen} />
          </details>
        </div>
      )}
      <div className="akey">
        {CATEGORIES.map((c) => (
          <span key={c.key}>
            <CatIcon cat={c.key} />
            {T(c.label)}
          </span>
        ))}
        <span>
          <i className="kpend" />
          {T('Venter på svar')}
        </span>
      </div>
    </>
  );
}

/** The absences on screen as a list, one heading per period: whole names, type, status, one button each. */
function Agenda({
  all,
  users,
  days,
  tenantId,
  onOpen,
}: {
  all: ApiAbsence[];
  users: ApiUser[];
  days: string[];
  tenantId: number | undefined;
  onOpen: (a: ApiAbsence) => void;
}) {
  const groups = agendaOf(all, new Set(users.map((u) => u.id)), days);
  if (groups.length === 0) return <Empty>{T('Ingen fravær i denne perioden.')}</Empty>;
  return (
    <div className="fagenda" data-testid="cal-agenda">
      {groups.map((g) => {
        const n = new Set(g.items.map((a) => a.user_id)).size;
        const period = periodText(g.from, g.to);
        return (
          <section key={`${g.from}|${g.to}`} aria-label={period}>
            <h3>
              <span>{period}</span>
              <small>
                {n} {n === 1 ? T('ansatt') : T('ansatte')}
              </small>
            </h3>
            <ul>
              {g.items.map((a) => {
                const u = users.find((x) => x.id === a.user_id);
                const name = fullName(a.user ?? u);
                const typeName = a.absence_type?.name ?? T('Fravær');
                const team = teamsOf(u, tenantId)
                  .map((t) => t.name)
                  .join(', ');
                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      data-testid="agenda-record"
                      aria-label={`${T('Åpne')} ${name}, ${typeName}, ${period}`}
                      onClick={() => onOpen(a)}
                    >
                      <span>
                        <b>{name}</b>
                        <small>{[team, typeName].filter(Boolean).join(', ')}</small>
                        <StatusChip status={a.status} />
                      </span>
                      <Icon name="chevronRight" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
